import { GamescheduleMatchList } from "@/components/Gameschedule/GamescheduleMatchList";
import { getCurrentGameday, getCurrentSeason } from "@/helper/api";
import prisma from "@/helper/client";
import { getMatchWithTeams } from "@/helper/util";
import { MatchWithTeamsAndVenueAndGamedayAndSeason } from "@/models/Match";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

export async function getServerSideProps(context: any) {
  const { res } = context;
  res.setHeader(
    "Cache-Control",
    "public, max-age=300, stale-while-revalidate=360",
  );
  const gamedayQuery = context.query.gameday;
  const teamQuery = context.query.team;

  if (!gamedayQuery || !teamQuery || !context.query.season) {
    return;
  }

  // Parse URL filters
  const timeFilter = context.query.time as string | undefined; // "future" | "past"
  const limit = context.query.limit
    ? parseInt(context.query.limit as string)
    : undefined;
  const homeAway = context.query.homeaway as string | undefined; // "home" | "away"
  const enableLinks = context.query.links === "true"; // "true" to enable links (opens in new tab)

  const seasonQuery = context.query.season;
  let season: any;
  if (seasonQuery === "current") {
    season = await getCurrentSeason();
  } else {
    const [start, end] = seasonQuery.split("-");

    season = await prisma.season.findFirst({
      where: { startYear: parseInt(start), endYear: parseInt(end) },
      include: { bundesliga: true, pokal: true },
    });
  }
  if (!season) {
    return;
  }

  const bundesligaTeams = await prisma.leagueTeam.findMany({
    where: {
      DBStatus: "Active",
      league: "Bundesliga",
      leagueId: season.bundesliga?.id,
    },
    include: { seasonTeam: true },
  });

  const currentGamedayIndex =
    gamedayQuery === "current"
      ? await getCurrentGameday("Bundesliga", season.id)
      : gamedayQuery;

  const gameday = await prisma.gameday.findFirst({
    where: {
      index: parseInt(currentGamedayIndex),
      leagueId: season.bundesliga?.id,
      league: "Bundesliga",
    },
  });

  const selectedTeam =
    teamQuery === "all"
      ? "all"
      : bundesligaTeams.find((team) => team.seasonTeam.id === teamQuery);
  if (!selectedTeam) {
    return;
  }

  // Build Prisma where clause with filters
  const now = Math.floor(Date.now() / 1000);

  // Build team filter
  let teamFilter: any = undefined;
  if (selectedTeam !== "all") {
    if (homeAway === "home") {
      teamFilter = { homeTeamId: selectedTeam.id };
    } else if (homeAway === "away") {
      teamFilter = { awayTeamId: selectedTeam.id };
    } else {
      teamFilter = {
        OR: [{ homeTeamId: selectedTeam.id }, { awayTeamId: selectedTeam.id }],
      };
    }
  }

  // Build time filter
  let timeFilterClause: any = undefined;
  if (timeFilter === "future") {
    timeFilterClause = { timeStamp: { gte: now } };
  } else if (timeFilter === "past") {
    timeFilterClause = { timeStamp: { lt: now } };
  }

  const matches = gameday
    ? await prisma.match.findMany({
        where: {
          gamedayId: selectedTeam === "all" ? gameday?.id : undefined,
          DBStatus: "Active",
          ...teamFilter,
          ...timeFilterClause,
        },
        include: {
          gameday: true,
          venue: true,
        },
        orderBy: { timeStamp: timeFilter === "past" ? "desc" : "asc" },
        take: limit,
      })
    : [];

  const fullMatches = matches.map((m) => getMatchWithTeams(bundesligaTeams, m));

  return {
    props: {
      matches: fullMatches.map((match) => ({
        ...match,
        season,
      })),
      enableLinks,
      ...(await serverSideTranslations(context.locale)),
    },
  };
}

interface PageProps {
  matches: MatchWithTeamsAndVenueAndGamedayAndSeason[];
  enableLinks: boolean;
}
export default function Page({ matches, enableLinks }: PageProps) {
  return (
    <GamescheduleMatchList
      baseUrl={`https://www.ttbl.de`}
      matches={matches}
      disableLinks={!enableLinks}
      openInNewTab={enableLinks}
    />
  );
}
